import { useEffect } from 'react'
import { TIER1_DOCS, DOC_SECTIONS, SHORT_BLOCK_LABELS, type DocEntry } from '@/docs/tier1Docs'

/**
 * Global Documentation / User Guide. Renders the shared docs registry
 * (tier1Docs) grouped by section — the same content that powers the header ⓘ
 * drawer, plus the User-Guide methodology block. Anchors match the drawer's
 * "Read full documentation" links (#<key>).
 */
export default function Documentation() {
  // Scroll to the #<key> anchor on load and on hash change (from the ⓘ drawer).
  useEffect(() => {
    const scrollToHash = () => {
      const id = window.location.hash.replace('#', '')
      if (!id) return
      const el = document.getElementById(id)
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
    scrollToHash()
    window.addEventListener('hashchange', scrollToHash)
    return () => window.removeEventListener('hashchange', scrollToHash)
  }, [])

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      <header>
        <h1 className="text-2xl font-bold">Documentation · User Guide</h1>
        <p className="text-muted-foreground mt-1 max-w-2xl">
          What each screen is for, how to use it, how to read the results, where the data comes from, and its limits.
        </p>
      </header>

      <nav className="bg-card border border-border rounded-lg p-4 text-sm">
        {DOC_SECTIONS.map((section) => (
          <div key={section} className="mb-3 last:mb-0">
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">{section}</div>
            <div className="flex flex-wrap gap-x-4 gap-y-1">
              {TIER1_DOCS.filter((d) => d.section === section).map((d) => (
                <a key={d.key} href={`#${d.key}`} className="text-primary hover:underline">{d.title}</a>
              ))}
            </div>
          </div>
        ))}
      </nav>

      {DOC_SECTIONS.map((section) => (
        <section key={section} className="space-y-4">
          <h2 className="text-lg font-semibold border-b border-border pb-2">{section}</h2>
          {TIER1_DOCS.filter((d) => d.section === section).map((d) => (
            <DocCard key={d.key} d={d} />
          ))}
        </section>
      ))}

      <p className="text-xs text-muted-foreground border-t border-border pt-4">
        Tier 1 — the 12 core screens. Tier 2/3 screens, a Getting-Started chapter, a data &amp; methodology overview, a glossary and a known-limits page follow the same template.
      </p>
    </div>
  )
}

function DocCard({ d }: { d: DocEntry }) {
  return (
    <article id={d.key} className="bg-card border border-border rounded-lg p-5 scroll-mt-20">
      <h3 className="text-lg font-semibold mb-3">{d.title}</h3>
      <dl className="space-y-2.5">
        {SHORT_BLOCK_LABELS.map(({ key, label }) => (
          <div key={key} className="grid grid-cols-1 sm:grid-cols-[130px_1fr] gap-1 sm:gap-3">
            <dt className="text-[11px] font-medium uppercase tracking-wider text-primary sm:pt-0.5">{label}</dt>
            <dd className="text-sm text-muted-foreground m-0">{d.short[key]}</dd>
          </div>
        ))}
      </dl>
      <div className="mt-3 pt-3 border-t border-border">
        <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground mb-1">Methodology</div>
        <p className="text-sm text-muted-foreground m-0">{d.methodology}</p>
      </div>
    </article>
  )
}
